import { formatLocalizedNumber, numberFormatter } from "@payping/utilities-shared/number";
import { useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CuToman } from "@payping/icons";
import { numberToPersianWords } from "@payping/utils/number";
import {
  Button,
  Divider,
  Flex,
  InputNumber,
  LegacyCard,
  LegacyForm,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import classNames from "classnames";
import { FC } from "react";

type FormInputs = {
  credit: number;
};

interface InitSettingCreditProps {
  onFinish: () => void;
}
const BnplActivationPageFormInitSettingCredit: FC<InitSettingCreditProps> = ({ onFinish }) => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { xl } = breakpoints;

  const [form] = LegacyForm.useForm<FormInputs>();
  const creditAmountWatch = LegacyForm.useWatch("credit", form);
  const contract = useAppSelector((state) => state.bnplMerchant.contract);

  const [walletCreditAmountState, setWalletCreditAmount] = useMessagedAsyncFn(
    async (contractId: number, walletCreditAmount: number) => {
      const res = await bnplMerchantApis.setWalletCreditAmount({
        contractId,
        walletCreditAmount,
      });
      onFinish();
      return res;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleFinishForm = async (data: FormInputs) => {
    const contractId = contract?.contractId;
    if (!contractId) return;
    await setWalletCreditAmount(contractId, data.credit);
  };

  const creditAmountWords = () => {
    try {
      const result = numberToPersianWords(creditAmountWatch);
      if (typeof result === "string") {
        return result;
      }
      return null;
    } catch {
      return null;
    }
  };

  return (
    <Flex className={classNames(["tw:w-full"], { "tw:pt-8!": !xl })} gap={"32px"} vertical={!xl}>
      <Flex className={classNames(["tw:w-full"], { "tw:py-8! tw:pr-8!": xl, "tw:px-8!": !xl })}>
        <Flex className="tw:w-full tw:xl:max-w-122" vertical gap={"large"}>
          <Typography.Title level={5} className="tw:m-0!">
            {t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.title")}
          </Typography.Title>
          <Flex vertical className="tw:w-full">
            {(
              t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.content", {
                returnObjects: true,
              }) as string[]
            ).map((item, index) => (
              <Typography.Paragraph className="tw:m-0!" key={index}>
                {item}
              </Typography.Paragraph>
            ))}
          </Flex>
          <LegacyCard.Ant>
            <LegacyForm
              form={form}
              layout="vertical"
              onFinish={handleFinishForm}
              initialValues={{
                credit: contract?.totalCredit ?? 0,
              }}
            >
              <LegacyForm.Item
                className="tw:m-0!"
                name={"credit"}
                label={t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.form.credit.label")}
                rules={[
                  { required: true },
                  {
                    validator(_, value) {
                      if (value < 100_000) {
                        return Promise.reject(
                          new Error(
                            t("common.validations.min", {
                              name: t("shop.bnpl.activation.formDrawer.steps.initSettings.credit.form.credit.label2"),
                              min: `${formatLocalizedNumber({ value: 100_000, options: { maximumFractionDigits: 2 } })} ${t("common.tomans")}`,
                            }),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  creditAmountWatch <= 0 || !creditAmountWords() ? null : (
                    <div className="tw:mb-2! tw:mt-1!">
                      <Flex align="center">
                        <Typography.Text>{`${creditAmountWords()}`}</Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </div>
                  )
                }
              >
                <InputNumber
                  formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                  parser={(value) => numberFormatter.parse(value)}
                  className="tw:w-full!"
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                  step={1000}
                  addonAfter={<CuToman width={24} height={24} />}
                />
              </LegacyForm.Item>
            </LegacyForm>
          </LegacyCard.Ant>
          {xl ? (
            <Flex>
              <Button onClick={() => form.submit()} type="primary" loading={walletCreditAmountState.loading}>
                {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
              </Button>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
      {xl ? <Divider className="tw:m-0! tw:h-full" orientation="vertical" /> : null}
      <Flex className={classNames(["tw:min-w-88!"], { "tw:py-8! tw:pl-8!": xl, "tw:px-8!": !xl })}>
        <LegacyCard.Ant className="tw:h-fit tw:w-full tw:bg-color-bg-layout">
          <Flex vertical gap={"large"} className="tw:w-full" justify="center" align="center">
            <Typography.Text>{t("shop.bnpl.activation.formDrawer.steps.initSettings.finalCredit")}</Typography.Text>
            <Flex align="center" wrap>
              <Typography.Title level={2} className="tw:m-0!">
                {formatLocalizedNumber({ value: creditAmountWatch ?? 0, options: { maximumFractionDigits: 2 } })}
              </Typography.Title>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
        </LegacyCard.Ant>
      </Flex>
      {xl ? null : (
        <Flex className="tw:px-8!" gap={"middle"}>
          <Button onClick={() => form.submit()} type="primary" loading={walletCreditAmountState.loading}>
            {t("shop.bnpl.activation.formDrawer.saveAndContinue")}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default BnplActivationPageFormInitSettingCredit;
